import { useContext, useState } from 'react';
import { notification } from 'antd';
import { useLazyGetStudyQuery } from '@/state/slices/studies';
import { useCreateaddendumMutation } from '@/state/slices/reports';
import { SettingsContext } from '@/state/contexts';

type ActiveModal = 'studyInfo' | 'addendum' | null;

export default function useReportRowModals(studyUuid: string) {
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [triggerGetStudy, studyResult] = useLazyGetStudyQuery();
  const [createAddendum, { isLoading: addendumLoading }] =
    useCreateaddendumMutation();
  const { settings } = useContext(SettingsContext);

  const openStudyInfo = () => {
    setActiveModal('studyInfo');
    triggerGetStudy({ uuid: studyUuid });
  };

  const openAddendum = () => {
    if (!settings.showAddendumModal) {
      handleAddendumSubmit({
        reason: 'CONCURRENCE',
        text: '',
      }).catch(() => undefined);
      return;
    }

    setActiveModal('addendum');
    triggerGetStudy({ uuid: studyUuid });
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  async function handleAddendumSubmit({
    reason,
    text,
  }: {
    reason: string;
    text: string;
  }) {
    try {
      await createAddendum({
        description: text,
        reason,
        studyUuid,
      }).unwrap();
      notification.success({
        message: 'Addendum created',
        placement: 'bottomRight',
      });
      closeModal();
    } catch {
      notification.error({
        message: 'Failed to create addendum',
        placement: 'bottomRight',
      });
    }
  }

  return {
    activeModal,
    study: studyResult.data,
    studyLoading: studyResult.isLoading,
    addendumLoading,
    openStudyInfo,
    openAddendum,
    closeModal,
    handleAddendumSubmit,
  };
}
